import { cva } from 'class-variance-authority';
import { useLocale } from 'next-intl';
import React, { useEffect, useState } from 'react';

interface CountDownProps {
  time: number;
  className?: string;
  onCountDownFinish?: () => void;
  showTimeUnit?: boolean; // 是否显示时间单位
  style?: React.CSSProperties;
}

const countdownVariants = cva('flex items-center justify-start font-bb text-error', {
  variants: {
    locale: {
      en: 'text-12',
      de: 'text-12 w-[66px] pl-[2px]',
      es: 'text-16',
      fr: 'text-12 w-[60px] pl-[2px]',
      it: 'text-14',
      pt: 'text-16',
      fi: 'text-16',
      nl: 'text-14',
    },
  },
  defaultVariants: {
    locale: 'en',
  },
});

const CountDown: React.FC<CountDownProps> = ({
  time = 0,
  onCountDownFinish,
  className,
  showTimeUnit = false,
  style,
}) => {
  const [hr, setHr] = useState<string>('00');
  const [min, setMin] = useState<string>('00');
  const [sec, setSec] = useState<string>('00');
  const [remainingTime, setRemainingTime] = useState<number>(time);
  const locale = useLocale();

  // Update remainingTime when time prop changes
  useEffect(() => {
    setRemainingTime(time);
  }, [time]);

  useEffect(() => {
    // Don't start the countdown if time is 0
    if (remainingTime <= 0) {
      setHr('00');
      setMin('00');
      setSec('00');
      onCountDownFinish?.();
      return;
    }

    const hrNum = Math.floor((remainingTime / (60 * 60)) % 24);
    const minNum = Math.floor((remainingTime / 60) % 60);
    const secNum = Math.floor(remainingTime % 60);

    setHr(hrNum > 9 ? hrNum.toString() : `0${hrNum}`);
    setMin(minNum > 9 ? minNum.toString() : `0${minNum}`);
    setSec(secNum > 9 ? secNum.toString() : `0${secNum}`);

    const timerId = setTimeout(() => {
      setRemainingTime((prevTime) => prevTime - 1);
    }, 1000);

    return () => clearTimeout(timerId);
  }, [remainingTime, onCountDownFinish]);

  if (!hr || !min || !sec) {
    return null;
  }

  return (
    <div className={`${countdownVariants({ locale: locale as any })} ${className}`} style={style}>
      <span className="inline-block min-w-8 text-center">{hr}</span>
      <span className={`${showTimeUnit ? '' : 'px-1'}`}>{showTimeUnit ? 'H:' : ':'}</span>
      <span className="inline-block min-w-8 text-center">{min}</span>
      <span className={`${showTimeUnit ? '' : 'px-1'}`}>{showTimeUnit ? 'M:' : ':'}</span>
      <span className="inline-block min-w-8 text-center">{sec}</span>
      {showTimeUnit && (
        <>
          <span className={`${showTimeUnit ? '' : 'px-1'}`}>{showTimeUnit ? 'S' : ''}</span>
        </>
      )}
    </div>
  );
};

export default CountDown;
